import { useMemo } from 'react';

interface KeyValueEditorProps {
  /** Current value as an ordered list of [key, value] pairs. */
  pairs: Array<[string, string]>;
  onChange: (pairs: Array<[string, string]>) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  /** Optional helper text shown under the table. */
  hint?: string;
}

/**
 * Small add/remove rows editor for `Record<string, string>` fields. We keep
 * the order in the parent's array so empty rows survive across re-renders
 * (Object.entries would re-key on every keystroke). The parent converts to
 * a Record only when persisting.
 */
export function KeyValueEditor({
  pairs,
  onChange,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  hint,
}: KeyValueEditorProps) {
  const rows = useMemo<Array<[string, string]>>(
    () => (pairs.length === 0 ? [['', '']] : pairs),
    [pairs],
  );

  const updateRow = (index: number, key: string, value: string) => {
    const next = rows.map((p, i) => (i === index ? ([key, value] as [string, string]) : p));
    onChange(next);
  };
  const removeRow = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length === 0 ? [] : next);
  };
  const addRow = () => {
    onChange([...rows, ['', '']]);
  };

  return (
    <div className="flex-col" style={{ gap: 6 }}>
      {rows.map(([k, v], i) => (
        <div className="row" key={i} style={{ gap: 6 }}>
          <input
            value={k}
            onChange={(e) => updateRow(i, e.target.value, v)}
            placeholder={keyPlaceholder}
            style={{ flex: '0 0 30%' }}
          />
          <input
            value={v}
            onChange={(e) => updateRow(i, k, e.target.value)}
            placeholder={valuePlaceholder}
            style={{ flex: 1 }}
          />
          <button type="button" className="btn small btn-ghost" onClick={() => removeRow(i)} aria-label="Remove row">
            x
          </button>
        </div>
      ))}
      <div className="row">
        <button type="button" className="btn small" onClick={addRow}>
          Add row
        </button>
        {hint && <span className="muted small">{hint}</span>}
      </div>
    </div>
  );
}

/** Convert form state (array of pairs) into a Record, dropping empty keys. */
export function pairsToRecord(pairs: Array<[string, string]>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of pairs) {
    const key = k.trim();
    if (!key) continue;
    out[key] = v;
  }
  return out;
}

/** Reverse of `pairsToRecord` for initialising form state. */
export function recordToPairs(record: Record<string, string> | undefined): Array<[string, string]> {
  if (!record) return [];
  return Object.entries(record).map(([k, v]) => [k, String(v ?? '')] as [string, string]);
}
